import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  ResolveEmailDeliveryFailuresParams,
  ResolveEmailDeliveryFailuresResponse,
  resolveEmailDeliveryFailures,
} from '~/services/SailthruService';

import { emailDeliveryFailuresQueryKeys } from './useEmailDeliveryFailures';

function getResolutionSummaryMessage(response: ResolveEmailDeliveryFailuresResponse): {
  message: string;
  variant: 'success' | 'warning' | 'error';
} {
  const succeeded = response.result.items.filter((item) => item.status === 'SUCCESS').length;
  const failed = response.result.items.filter((item) => item.status === 'FAILED').length;

  if (failed === 0) {
    return {
      message: `Marked ${succeeded} email${succeeded === 1 ? '' : 's'} as resolved`,
      variant: 'success',
    };
  }

  if (succeeded === 0) {
    return {
      message: `Failed to mark ${failed} email${failed === 1 ? '' : 's'} as resolved`,
      variant: 'error',
    };
  }

  return {
    message: `Marked ${succeeded} email${succeeded === 1 ? '' : 's'} as resolved. ${failed} failed.`,
    variant: 'warning',
  };
}

export function useResolveEmailDeliveryFailures() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (params: ResolveEmailDeliveryFailuresParams) => resolveEmailDeliveryFailures(params),
    onSuccess: (response) => {
      const { message, variant } = getResolutionSummaryMessage(response);
      enqueueSnackbar(message, { variant });
      queryClient.invalidateQueries({ queryKey: emailDeliveryFailuresQueryKeys.all });
    },
    onError: () => {
      enqueueSnackbar('Failed to mark email delivery failures as resolved', { variant: 'error' });
    },
  });
}
